import { createContext } from 'react';
import type { Me } from '@/api/schemas/identity';
import type { Permission } from '@/auth/permissions';
import type { AuthAdapter } from '@/auth/AuthAdapter';

export interface AuthState {
  status: 'loading' | 'signed-out' | 'signed-in' | 'error';
  /**
   * True until the **first** resolve completes, and never again.
   *
   * `status` returns to `loading` on every later refresh — a role switch, a
   * re-read of `/me` — so it answers "is a request in flight", not "do we know
   * yet who this is". Only the second question may blank the screen: the shell
   * that unmounts itself on a refresh takes whatever the user was doing with
   * it, and the role switcher disappearing under the cursor is how that was
   * found.
   */
  booting: boolean;
  me: Me | null;
  error: Error | null;
  adapter: AuthAdapter;
  /**
   * Both resolve with the session they established. The caller needs it:
   * computing the landing route from the hook's own `has` would read the
   * permissions of the render the click happened in — which, coming from the
   * sign-in screen, are nobody's.
   */
  signInWithSso: () => Promise<Me | null>;
  signInWithLocal: (username: string, password: string) => Promise<Me | null>;
  signOut: () => Promise<void>;
  /** Re-read `/me` after the dev role switcher changes the session. */
  refresh: () => Promise<void>;
  has: (permission: Permission) => boolean;
  /** True while the caller sees only part of the facility. */
  scopeLimited: boolean;
}

export const AuthContext = createContext<AuthState | null>(null);
